import type { Schedule } from '../../services/FP_FulfillmentContextService/types';
import { hydrateSchedule } from './hyrdrateSchedule';
import { extractTenantDetails, findScopedTenant, generateHydrationError, isActiveAsset } from './utils';
import type { HydratedAddress, HydratedContact, HydratedSchedule, ValidationInfo } from '../types';
import { hydrateGenericBaseAsset } from './hydrateGenericBaseAssest';
import { hydrateAddress } from './addressHydration';
import type { PartnerAddressV2, PartnerContact, PartnerOrigin, PartnerTenant } from '@shipium-corp/shipium-ts-types';

export interface HydrateOriginParams {
  origin: PartnerOrigin;
  addresses: Record<string, PartnerAddressV2>;
  schedules: Record<string, Schedule>;
  contacts: Record<string, PartnerContact>;
  tenantList: PartnerTenant[];
}
const originAssetType = 'origin';
const generateErrors = (
  origin: PartnerOrigin,
  addressValidation: ValidationInfo,
  contactValidation: ValidationInfo,
  scheduleValidation: ValidationInfo,
) => {
  return [
    ...(!isActiveAsset(origin) ? [generateHydrationError('Not active', originAssetType, origin.originId)] : []),
    ...addressValidation.errors,
    ...contactValidation.errors,
    ...scheduleValidation.errors,
  ];
};

const generateWarnings = (
  addressValidation: ValidationInfo,
  contactValidation: ValidationInfo,
  scheduleValidation: ValidationInfo,
  returnsAddressValidation: ValidationInfo,
  returnsContactValidation: ValidationInfo,
) => {
  return [
    ...addressValidation.warnings,
    ...contactValidation.warnings,
    ...scheduleValidation.warnings,
    ...returnsAddressValidation.errors,
    ...returnsContactValidation.errors,
    ...returnsAddressValidation.warnings,
    ...returnsContactValidation.warnings,
  ];
};

export const hydrateOrigin = ({ origin, addresses, schedules, contacts, tenantList }: HydrateOriginParams) => {
  const foundAddress = hydrateAddress(tenantList, addresses[origin?.addressId ?? '']);
  const foundSchedule = hydrateSchedule(tenantList, schedules[origin?.scheduleId ?? '']);
  const foundContact = hydrateGenericBaseAsset(
    tenantList,
    'contacts',
    contacts[origin?.contactId ?? ''],
    contacts[origin?.contactId ?? '']?.contactId,
  );
  const foundReturnToContact = hydrateGenericBaseAsset(
    tenantList,
    'contacts',
    contacts[origin?.returnToContactId ?? ''],
    contacts[origin?.returnToContactId ?? '']?.contactId,
  );
  const foundReturnToAddress = hydrateAddress(tenantList, addresses[origin?.returnToAddressId ?? '']);
  const tenant = findScopedTenant(origin.originId, 'origins', tenantList);
  const errors = generateErrors(
    origin,
    foundAddress.validationInfo,
    foundContact.validationInfo,
    foundSchedule.validationInfo,
  );
  const warnings = generateWarnings(
    foundAddress.validationInfo,
    foundContact.validationInfo,
    foundSchedule.validationInfo,
    foundReturnToAddress.validationInfo,
    foundReturnToContact.validationInfo,
  );

  return {
    ...origin,
    ...(foundAddress.addressId ? { address: foundAddress as HydratedAddress } : {}),
    ...(foundSchedule.scheduleId ? { schedule: foundSchedule as HydratedSchedule } : {}),
    ...(foundContact.contactId ? { contact: foundContact as HydratedContact } : {}),
    ...(foundReturnToContact.contactId ? { returnToContact: foundReturnToContact as HydratedContact } : {}),
    ...(foundReturnToAddress.addressId ? { returnToAddress: foundReturnToAddress as HydratedAddress } : {}),
    ...(tenant ? { tenant: extractTenantDetails(tenant) } : {}),
    validationInfo: {
      errors,
      warnings,
      configurationValid: !errors.length,
      configurationComplete: !errors.length && !warnings.length,
    },
  };
};
